import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocations } from "@/hooks/use-locations";

interface LocationContextType {
  selectedLocationId: string | null; // null means "all locations"
  setSelectedLocationId: (locationId: string | null) => void;
  locations: any[];
  loading: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const STORAGE_KEY = "buukd_selected_location_id";

export function LocationProvider({ children }: { children: ReactNode }) {
  const { locations, loading } = useLocations();
  const [selectedLocationId, setSelectedLocationIdState] = useState<string | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    // For single-location businesses, always keep selectedLocationId as null (no filtering)
    if (locations.length <= 1) {
      setSelectedLocationIdState(null);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // For multi-location businesses, load from localStorage if available
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      // Validate that the stored location still exists
      const locationExists = locations.some((loc) => loc.id === stored);
      if (locationExists) {
        setSelectedLocationIdState(stored);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [locations]);

  // For single-location businesses, keep selectedLocationId as null (show all)
  // This ensures no filtering happens when there's only one location
  // Only auto-select if user explicitly chooses a location

  const setSelectedLocationId = (locationId: string | null) => {
    setSelectedLocationIdState(locationId);
    if (locationId) {
      localStorage.setItem(STORAGE_KEY, locationId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <LocationContext.Provider
      value={{
        selectedLocationId,
        setSelectedLocationId,
        locations,
        loading,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    // Return default values if not in LocationProvider (for public routes)
    return {
      selectedLocationId: null,
      setSelectedLocationId: () => {},
      locations: [],
      loading: false,
    };
  }
  return context;
}
